import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

const LOGO_SIZE = 52;
const QR_SIZE = 240;

function safeFileName(title) {
  const base = (title || 'qrcode').replace(/[\\/:*?"<>|]/g, '').trim();
  return `${base || 'qrcode'}.png`;
}

function copyText(text, done, fail) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(fail);
  } else {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta); done();
    } catch { fail(); }
  }
}

export default function QrModal({ url, title, onClose, previewUrl, links, accent, copyContent, copyContentLabel }) {
  const ac = accent || '#FA1C51';
  const canvasRef = useRef(null);
  const [copyState, setCopyState] = useState('idle'); // idle | done | fail
  const [copiedLink, setCopiedLink] = useState('');

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = safeFileName(title);
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleCopyLink = () => {
    const done = () => { setCopyState('linkdone'); setTimeout(() => setCopyState('idle'), 2000); };
    const fail = () => { setCopyState('fail'); setTimeout(() => setCopyState('idle'), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(fail);
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta); done();
      } catch { fail(); }
    }
  };

  const handleCopy = () => {
    const canvas = canvasRef.current;
    if (!canvas || !navigator.clipboard || !window.ClipboardItem) {
      setCopyState('fail');
      setTimeout(() => setCopyState('idle'), 2000);
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) { setCopyState('fail'); setTimeout(() => setCopyState('idle'), 2000); return; }
      navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
        .then(() => { setCopyState('done'); setTimeout(() => setCopyState('idle'), 2000); })
        .catch(() => { setCopyState('fail'); setTimeout(() => setCopyState('idle'), 2000); });
    }, 'image/png');
  };

  useEffect(() => {
    if (!canvasRef.current || !url) return;
    QRCode.toCanvas(canvasRef.current, url, {
      width: QR_SIZE,
      margin: 2,
      errorCorrectionLevel: 'H',
    }, () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const logo = new Image();
      logo.src = process.env.PUBLIC_URL + '/favicon.png';
      logo.onload = () => {
        const x = (QR_SIZE - LOGO_SIZE) / 2;
        const y = (QR_SIZE - LOGO_SIZE) / 2;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(x - 4, y - 4, LOGO_SIZE + 8, LOGO_SIZE + 8, 10);
        ctx.fill();
        ctx.drawImage(logo, x, y, LOGO_SIZE, LOGO_SIZE);
      };
    });
  }, [url]);

  if (!url) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>{title}</div>
        <canvas ref={canvasRef} style={styles.canvas} />
        <div style={styles.urlText}>{url}</div>
        {copyContent && (
          <button
            style={{ ...styles.copyLinkBtn, background: '#7C3AED' }}
            onClick={() => copyText(copyContent,
              () => { setCopyState('contentdone'); setTimeout(() => setCopyState('idle'), 2000); },
              () => { setCopyState('fail'); setTimeout(() => setCopyState('idle'), 2000); })}
          >
            {copyState === 'contentdone' ? (copyContentLabel || '文案') + '已复制 ✓' : '复制' + (copyContentLabel || '博主文案')}
          </button>
        )}
        <button style={{ ...styles.copyLinkBtn, background: ac }} onClick={handleCopyLink}>
          {copyState === 'linkdone' ? '链接已复制 ✓' : '复制链接'}
        </button>
        {previewUrl && (
          <button style={{ ...styles.previewBtn, borderColor: ac, color: ac }} onClick={() => window.open(previewUrl, '_blank')}>
            预览（商家视角）
          </button>
        )}
        <div style={styles.btnRow}>
          <button style={{ ...styles.primaryBtn, background: ac }} onClick={handleDownload}>下载图片</button>
          <button style={{ ...styles.outlineBtn, borderColor: ac, color: ac }} onClick={handleCopy}>
            {copyState === 'done' ? '已复制' : copyState === 'fail' ? '复制失败' : '复制图片'}
          </button>
        </div>
        {Array.isArray(links) && links.length > 0 && (
          <div style={styles.linksBox}>
            <div style={styles.linksTitle}>单独发给某位博主</div>
            {links.map((l) => (
              <div key={l.url} style={styles.linkRow}>
                <span style={styles.linkLabel}>{l.label}</span>
                <button
                  style={styles.linkCopy}
                  onClick={() => copyText(l.url, () => { setCopiedLink(l.url); setTimeout(() => setCopiedLink(''), 2000); }, () => {})}
                >{copiedLink === l.url ? '已复制' : '复制'}</button>
              </div>
            ))}
          </div>
        )}
        <button style={styles.closeBtn} onClick={onClose}>关闭</button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 12, padding: '28px 24px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
    minWidth: 280,
  },
  modalTitle: { fontSize: 16, fontWeight: 600, color: '#111' },
  canvas:     { display: 'block' },
  urlText:    { fontSize: 11, color: '#999', wordBreak: 'break-all', textAlign: 'center', maxWidth: 240 },
  copyLinkBtn: {
    width: '100%', padding: '10px 0', border: 'none', borderRadius: 8,
    background: '#FA1C51', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  previewBtn: {
    width: '100%', padding: '10px 0', border: '1px solid #7C3AED', borderRadius: 8,
    background: '#fff', color: '#7C3AED', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  linksBox: { width: '100%', borderTop: '1px solid #f0f0f0', paddingTop: 12 },
  linksTitle: { fontSize: 12, color: '#9ca3af', marginBottom: 8 },
  linkRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0' },
  linkLabel: { fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  linkCopy: { padding: '4px 12px', border: '1px solid #7C3AED', borderRadius: 6, background: '#fff', color: '#7C3AED', cursor: 'pointer', fontSize: 12, flexShrink: 0 },
  btnRow:     { display: 'flex', gap: 12, width: '100%' },
  primaryBtn: {
    flex: 1, padding: '8px 0', border: 'none', borderRadius: 6,
    background: '#FA1C51', color: '#fff', cursor: 'pointer', fontSize: 14,
  },
  outlineBtn: {
    flex: 1, padding: '8px 0', border: '1px solid #FA1C51', borderRadius: 6,
    background: '#fff', color: '#FA1C51', cursor: 'pointer', fontSize: 14,
  },
  closeBtn: {
    marginTop: 4, padding: '8px 32px', border: '1px solid #e5e7eb',
    borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14,
  },
};
